import React, { useState, useEffect, useRef } from "react";
import { FaWhatsapp, FaPhoneAlt, FaFacebookF, FaInstagram, FaYoutube, FaArrowUp } from "react-icons/fa";
import { MdTranslate } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ru', label: 'Русский' },
  { code: 'it', label: 'Italiano' },
];

const FloatingContactButtons = () => {
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(i18n.language);
  const langRef = useRef(null);

  useEffect(() => {
    const handleLangChange = (lng) => setLang(lng);
    i18n.on('languageChanged', handleLangChange);
    return () => i18n.off('languageChanged', handleLangChange);
  }, [i18n]);

  useEffect(() => {
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollButtons(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const iconClass = `
    flex items-center justify-center 
    w-10 h-10 sm:w-12 sm:h-12 
    rounded-xl sm:rounded-2xl 
    transition-all duration-500
    shadow-xl
  `;

  return (
    <>
      {/* Right Side Buttons: Call + Scroll-to-top */}
      <div className="fixed bottom-8 right-6 z-[9999] flex flex-col gap-4">
        <AnimatePresence>
          {showScrollButtons && (
            <>
              <motion.a
                initial={{ opacity: 0, y: 20, scale: 0.5 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.5 }}
                href="tel:+919993910094"
                className={`${iconClass} bg-orange-main text-white hover:bg-orange-light hover:-translate-y-2 shadow-orange-main/20`}
                aria-label={t('call_now')}
              >
                <FaPhoneAlt className="text-lg" />
              </motion.a>

              <motion.button
                initial={{ opacity: 0, y: 20, scale: 0.5 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.5 }}
                onClick={scrollToTop}
                className={`${iconClass} bg-white text-gray-dark hover:bg-gray-50 border border-gray-100 hover:-translate-y-2 shadow-black/5`}
                aria-label={t('scroll_top')}
              >
                <FaArrowUp className="text-lg" />
              </motion.button>
            </>
          )}
        </AnimatePresence>
      </div>

      {/* Left Side Buttons: Language Switcher + Social Media */}
      <div className="fixed bottom-8 left-6 z-[9999] flex flex-col gap-3">
        {/* Language Switcher Dropdown */}
        <div ref={langRef} className="relative">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: 8, scale: 1.1 }}
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-xl text-white bg-orange-main shadow-lg shadow-orange-main/10 transition-all duration-300"
            aria-label="Switch Language"
          >
            <MdTranslate className="text-lg sm:text-[30px]" />
          </motion.button>

          <AnimatePresence>
            {langOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                className="absolute bottom-full left-0 mb-2 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 min-w-[150px]"
              >
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      lang === l.code
                        ? 'bg-orange-main text-white font-semibold'
                        : 'text-gray-700 hover:bg-orange-50'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <FloatingIcon
          href="https://www.facebook.com/profile.php?id=100064103830605"
          icon={<FaFacebookF />}
          color="bg-orange-main/90"
          shadow="shadow-orange-main/10"
        />
        <FloatingIcon
          href="https://www.instagram.com/routeskhajuraho/"
          icon={<FaInstagram />}
          color="bg-orange-main/80"
          shadow="shadow-orange-main/10"
        />
        <FloatingIcon
          href="https://www.youtube.com/@khajurahoroutes8681"
          icon={<FaYoutube />}
          color="bg-orange-main/70"
          shadow="shadow-orange-main/10"
        />
        <FloatingIcon
          href="https://wa.me/919993910094"
          icon={<FaWhatsapp />}
          color="bg-[#25D366]"
          shadow="shadow-[#25D366]/30"
        />
      </div>
    </>
  );
};

const FloatingIcon = ({ href, icon, color, shadow }) => (
  <motion.a
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    whileHover={{ x: 8, scale: 1.1 }}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`
      flex items-center justify-center 
      w-9 h-9 sm:w-11 sm:h-11 
      rounded-xl text-white 
      ${color} ${shadow} shadow-lg 
      transition-all duration-300
    `}
  >
    <span className="text-base sm:text-lg">{icon}</span>
  </motion.a>
);

export default FloatingContactButtons;
